import type { BrushStroke } from "./bishun-drawing.js";
import { setBrush, strokeMilliseconds } from "./bishun-playback.js";

/**
 * A stroke shown being written, in the cue's blue, on a drill square.
 *
 * The cue writes its stroke in as it moves to it, in the first stage, and a
 * stroke the reader missed is written in for them to see in every stage that
 * snaps. Both use the second brush `xiezi-layer.ts` draws per stroke, and this
 * is the brush moving. `xiezi-square.ts` holds one per square and hands it
 * each character's brushes as the character comes in.
 *
 * **Slowly, and over and over.** The brush moves at a third of the speed the
 * 笔顺 figure writes at, holds, and writes the stroke again, until the reader
 * draws something. A stroke written once at the figure's pace was over before
 * a reader had looked at it. Hugh asked for both on 2026-10-15.
 *
 * **It can wait.** A character written whole flashes green, and a cue starting
 * under the flash read as part of it. {@link StrokeDemonstration.pause} holds
 * the next stroke back until the flash has played.
 */

/** How many times slower than the 笔顺 figure the brush moves. */
const slower = 3;

/** How long a written stroke stays before it is written again. */
const hold = 900;

/** The gap between the stroke vanishing and the brush starting again. */
const gap = 250;

/** The demonstration brushes of the character in one square. */
export class StrokeDemonstration {
  #demos: readonly BrushStroke[] = [];
  #cued: number | undefined;
  #timers: ReturnType<typeof setTimeout>[] = [];
  #pausedUntil = 0;

  /**
   * Take a new character's brushes, with none of them written or cued.
   * @param demos - The demonstration brush for each stroke, in order.
   */
  reset(demos: readonly BrushStroke[]): void {
    this.stop();
    this.#demos = demos;
    this.#cued = undefined;
  }

  /**
   * Write the stroke the cue points to, over and over. A cue that has not
   * moved goes on as it was, and no cue stops whatever was being written.
   * @param at - Which stroke, or nothing where the cue points out none.
   */
  cue(at: number | undefined): void {
    if (at === undefined) {
      this.#cued = undefined;
      this.stop();
    } else if (at !== this.#cued) {
      this.#cued = at;
      this.#writeCued(at);
    }
  }

  /**
   * Write one stroke as a brush moving along it, and again until stopped, at
   * once. A stroke the reader has just missed is shown whatever is flashing.
   * @param at - Which stroke.
   */
  demonstrate(at: number): void {
    this.stop();
    this.#write(at);
  }

  /** Write the stroke the cue points to, once any pause is over. */
  #writeCued(at: number): void {
    this.stop();

    const wait = this.#pausedUntil - Date.now();

    if (wait > 0) {
      this.#timers.push(setTimeout(() => this.#write(at), wait));
    } else {
      this.#write(at);
    }
  }

  /**
   * Hold the brush back for a while, and start the stroke the cue points to
   * once it has passed.
   * @param milliseconds - How long.
   */
  pause(milliseconds: number): void {
    this.#pausedUntil = Date.now() + milliseconds;

    if (this.#cued !== undefined) {
      this.#writeCued(this.#cued);
    }
  }

  /** Every brush unwritten at once, and its timers dropped. */
  stop(): void {
    for (const timer of this.#timers) {
      clearTimeout(timer);
    }

    this.#timers = [];

    for (const demo of this.#demos) {
      unwrite(demo);
    }
  }

  /** Write the stroke, then set it going again once it has been seen. */
  #write(at: number): void {
    const demo = this.#demos[at];

    // Whatever brought the brush here has fired, being the last round's own.
    this.#timers = [];

    if (demo === undefined) {
      return;
    }

    const duration = strokeMilliseconds(demo.length) * slower;

    unwrite(demo);
    // Settled at unwritten before it is asked to move, or the browser
    // coalesces the two and the stroke appears whole. The same move
    // `playStrokes` makes in `bishun-playback.ts`.
    demo.brush.getBoundingClientRect();
    demo.brush.style.setProperty(
      "--cb-stroke-duration",
      `${String(duration)}ms`,
    );
    setBrush(demo, true);
    this.#timers.push(
      setTimeout(() => {
        unwrite(demo);
      }, duration + hold),
      setTimeout(
        () => {
          this.#write(at);
        },
        duration + hold + gap,
      ),
    );
  }
}

/** One brush unwritten at once. */
function unwrite(demo: BrushStroke): void {
  demo.brush.style.setProperty("--cb-stroke-duration", "0ms");
  setBrush(demo, false);
}
